import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  UserPlus,
  AlertTriangle,
  CheckCircle,
  Copy,
  Eye,
  EyeOff,
  AlertCircle,
  Shield
} from 'lucide-react';

const CredentialGenerationModal = ({ 
  isOpen, 
  onClose, 
  employee, 
  onGenerate, 
  result, 
  isGenerating 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState({});

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [field]: true }));
      setTimeout(() => {
        setCopied(prev => ({ ...prev, [field]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleGenerate = () => {
    onGenerate(employee.id);
  };

  const handleClose = () => {
    setCopied({});
    setShowPassword(false);
    onClose();
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <UserPlus className="w-5 h-5 mr-2 text-purple-600" />
            Generate User Credentials
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Employee Info */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-sm">
            <p className="font-medium text-gray-900">{employee.full_name}</p>
            <p className="text-gray-600">{employee.emp_code}</p>
            <p className="text-gray-600">
              {(employee.employee_type_display || employee.employee_type || 'Employee').replace(/_/g, ' ')} Employee
            </p>
          </div>
        </div>

        {/* Results Display */}
        {result && (
          <div className="mb-4">
            {result.success ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-green-800 mb-2">
                      Credentials Generated Successfully!
                    </h4>
                    
                    {/* Username */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-green-700 mb-1">
                        Username
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={result.username}
                          readOnly
                          className="flex-1 px-3 py-2 text-sm bg-white border border-green-300 rounded-md"
                        />
                        <button
                          onClick={() => copyToClipboard(result.username, 'username')}
                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-md transition-colors"
                          title="Copy username"
                        >
                          {copied.username ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Password */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-green-700 mb-1">
                        Password
                      </label>
                      <div className="flex items-center space-x-2">
                        <div className="relative flex-1">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={result.password}
                            readOnly
                            className="w-full px-3 py-2 pr-10 text-sm bg-white border border-green-300 rounded-md"
                          />
                          <button
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-green-600 hover:text-green-800"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <button
                          onClick={() => copyToClipboard(result.password, 'password')}
                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-md transition-colors"
                          title="Copy password"
                        >
                          {copied.password ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Security Warning */}
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <div className="flex items-start">
                        <Shield className="w-4 h-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-medium mb-1">Important Security Notice:</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Save these credentials securely - they won't be shown again</li>
                            <li>• The user will be required to change the password on first login</li>
                            <li>• Share these credentials only with the intended employee</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800 mb-1">
                      {result.message}
                    </h4>
                    {result.warning && (
                      <p className="text-sm text-red-700">{result.warning}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Warning for Contractual Employees */}
        {!result && employee.employee_type === 'contractual' && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-amber-800 mb-1">
                  Contractual Employee Warning
                </h4>
                <p className="text-sm text-amber-700">
                  Contractual employees are not eligible for user accounts. 
                  Only permanent employees can have login credentials.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Warning for Incomplete Documents */}
        {!result && employee.documents_complete === false && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-red-800 mb-1">
                  Incomplete Documents
                </h4>
                <p className="text-sm text-red-700">
                  Upload all mandatory documents before generating employee credentials.
                  {employee.documents_missing !== undefined && (
                    <span className="block mt-1">
                      {employee.documents_mandatory_uploaded || employee.documents_uploaded} / {employee.documents_required} mandatory documents uploaded
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {result?.success ? 'Done' : 'Cancel'}
          </button>
          
          {!result && (
            <button
              onClick={handleGenerate}
              disabled={isGenerating || employee.employee_type === 'contractual' || employee.documents_complete === false}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Generate Credentials
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default CredentialGenerationModal;